import React, { useEffect, useState } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";

import { useRecoilState } from 'recoil';
import { subAtom, outletAtom } from 'core/config/atoms';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';
import animPage from 'assets/anim/anim-pageload.json';

import ServiceFloor from 'services/serviceFloor';
import ServiceBooking from 'services/serviceBooking';
import ServiceOutlet from 'services/serviceOutlet';

export default function ComponentBookingForm(props) {

    const [outlet] = useRecoilState(outletAtom);
    const [sub] = useRecoilState(subAtom);

    const [types, setTypes] = useState([]);
    const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);

    const [taxSetting, setTax] = useState({});

    const [loading, setLoading] = useState(true);
    const [submit, setSubmit] = useState(false);

    const floorService = new ServiceFloor();
    const bookingService = new ServiceBooking();
    const outletService = new ServiceOutlet();

    const formVSchema = Yup.object().shape({
        bookingName: Yup.string().required('This information is required'),
        bookingEmail: Yup.string().required('This information is required').email('Please enter valid email address'),
        bookingPhone: Yup.string().required('This information is required').matches(/^\d{10}$/, 'Phone number must be 10 digits'),
        checkIn: Yup.string().required('This information is required'),
        checkOut: Yup.string().required('This information is required'),
        nop: Yup.number().required('This information is required'),
        nor: Yup.number().required('This information is required'),
        total: Yup.number().required('This information is required'),
        extraCharges: Yup.number().required('This information is required'),
        discount: Yup.number().required('This information is required'),
        grand: Yup.number().required('This information is required'),
        paid: Yup.number().required('This information is required'),
        paymentMode: Yup.string().required('This information is required'),
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, resetForm, setValues } = useFormik({
        initialValues: {
            bookingName: '',
            bookingPhone: '',
            bookingEmail: '',
            checkIn: '',
            checkOut: '',
            nop: 0,
            nor: 0,
            total: 0,
            extraCharges: 0,
            discount: 0,
            tax: 0,
            grand: 0,
            paid: 0,
            paymentMode: '',
        },
        validationSchema: formVSchema,
        enableReinitialize: true,
        onSubmit: values => {
            setSubmit(true);
            var body = {
                "bookName": values.bookingName,
                "bookEmail": values.bookingEmail,
                "bookPhone": values.bookingPhone,
                "bookType": props.type,
                "nop": values.nop,
                "nor": values.nor,
                "checkIn": moment(values.checkIn).toISOString(),
                "checkOut": moment(values.checkOut).toISOString(),
                "roomTypes": selectedRoomTypes,
                "total": values.total,
                "extraCharge": values.extraCharges,
                "discount": values.discount,
                "tax": values.tax,
                "finaltotal": values.grand,
                "paid": values.paid,
                "paymentStatus": getPaymentSts(values),
                "outletid": outlet.outletid.id,
                "outlet": outlet.outletid.outletid,
                "paymentMode": values.paymentMode,
            };
            bookingService.createBooking(body, '').then((res) => {
                if (res.status) {
                    resetForm();
                    setSelectedRoomTypes([]);
                    props.onClose();
                    toast.success('New booking added.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                } else {
                    toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                }
                setSubmit(false);
            });
        }
    });

    const getPaymentSts = (val) => {
        if (val.grand === val.paid) {
            return "paid";
        }
        if (val.grand > val.paid) {
            return "partial";
        }
        return "pending";
    }

    const calculateTotal = () => {
        const start = moment(values.checkIn);
        const end = moment(values.checkOut);
        const totalNights = end.diff(start, 'days');

        let totalBasePrice = 0;
        let totalExtraCharge = 0;
        let totalMaxCapacity = 0;
        let totalExtraCapacity = 0;
        let extraPeople = values.nop;
        let extraPerCharge = 0;

        selectedRoomTypes.forEach(roomID => {
            const room = types.filter(room => room._id === roomID)[0];
            const { priceVariationType, pricing, maxCapacity, extraCapacity, extraPersonCharge } = room;
            let roomPrice = 0;

            for (let i = 0; i < totalNights; i++) {
                const day = moment(values.checkIn).add(i, 'days');
                const dayName = day.format('ddd').toLowerCase();
                const isWeekend = ['sat', 'sun'].includes(dayName);

                switch (priceVariationType) {
                    case 'fixed':
                        roomPrice += pricing.fixed;
                        break;
                    case 'split':
                        roomPrice += isWeekend ? pricing.split.weekend : pricing.split.weekday;
                        break;
                    case 'individual':
                        roomPrice += pricing.individual[dayName] || 0;
                        break;
                    default:
                        break;
                }
            }

            totalBasePrice += roomPrice;
            totalMaxCapacity += maxCapacity;
            totalExtraCapacity += extraCapacity;
            if (extraPerCharge < extraPersonCharge) {
                extraPerCharge = extraPersonCharge;
            }
        });

        if (extraPeople > totalMaxCapacity) {
            const overflow = Math.min(extraPeople - totalMaxCapacity, totalExtraCapacity);
            totalExtraCharge += overflow * extraPerCharge * totalNights;
        }

        calculateGradTotal(totalBasePrice, totalExtraCharge);
    }

    const calculateGradTotal = (totalBasePrice, totalExtraCharge) => {
        let grandTotal = parseFloat(totalBasePrice) + parseFloat(totalExtraCharge);
        let taxAmount = 0;

        if (values.discount > 0) {
            grandTotal -= values.discount;
        }

        if (values.tax > 0) {
            if (Object.keys(taxSetting).length !== 0) {
                switch (taxSetting.Room_TAX_TYPE) {
                    case "na":
                        taxAmount = 0;
                        break;
                    case "inclusive":
                        if (values.tax > 0) {
                            taxAmount =
                                grandTotal -
                                grandTotal / (1 + taxSetting.Room_Percentage / 100);
                        }
                        break;
                    case "exclusive":
                        taxAmount = grandTotal * (taxSetting.Room_Percentage / 100);
                        break;
                    case "overall":
                    default:
                        taxAmount = grandTotal * (values.tax / 100);
                        break;
                }
            } else {
                taxAmount = grandTotal * (values.tax / 100);
            }
            grandTotal += taxAmount;
        }

        setValues({ ...values, grand: grandTotal, total: totalBasePrice, extraCharges: totalExtraCharge });
    }

    const handleRoomTypeChange = (index, value) => {
        const newTypes = [...selectedRoomTypes];
        newTypes[index] = value;
        setSelectedRoomTypes(newTypes);
    };

    const loadData = () => {
        setLoading(true);
        (async function () {
            var res = await floorService.getAllRoomType({}, '');
            setTypes(res.data);
            if (sub.features.isTax) {
                var res = await outletService.getOutletById({ 'outletid': outlet.outletid.outletid });
                const taxSet = res.data.tax;
                setTax(taxSet);
                if (props.type === "spot") {
                    setValues({ ...values, checkIn: moment().format('yyyy-MM-DD'), tax: taxSet.Room_Percentage });
                } else {
                    setValues({ ...values, tax: taxSet.Room_Percentage });
                }
            } else {
                if (props.type === "spot") {
                    setValues({ ...values, checkIn: moment().format('yyyy-MM-DD') })
                }
            }
            setLoading(false);
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (selectedRoomTypes.length !== 0) {
            calculateTotal();
        }
    }, [selectedRoomTypes, values.nop, values.checkIn, values.checkOut, values.discount, values.tax]);

    return (
        <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
            <div className='bg-white w-[70%] min-h-screen absolute right-0 p-[20px]'>
                <ScrollArea>
                    <div className='px-2'>
                        <div className='flex items-center justify-between text-2xl'>
                            <h1 className='font-sans font-medium text-gray-900 uppercase'>{props.type === "spot" ? "Spot" : "Advance"} Booking Form</h1>
                            <div className='w-7 h-7 text-gray-700 flex items-center justify-center border border-black rounded-full hover:bg-black hover:text-white cursor-pointer' onClick={() => {
                                resetForm();
                                setSelectedRoomTypes([]);
                                props.onClose();
                            }}>
                                <span><i className="las la-times text-xl cursor-pointer"></i></span>
                            </div>
                        </div>
                        <hr className='my-2' />
                        {
                            loading && <div className='flex items-center justify-center w-full h-full'>
                                <Lottie animationData={animPage} className="w-40 h-40" loop={true} />
                            </div>
                        }
                        {
                            !loading && <form className='mt-4 grid grid-cols-12 gap-4' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>
                                <div className="col-span-12 font-medium">
                                    <p>Booking information</p>
                                    <div className='w-full h-[1px] bg-gray-400 mt-1'></div>
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Booking Person Name<sup className="text-red-600">*</sup></label>
                                    <input type="text" id="bookingName" value={values.bookingName} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Jhon Deo" onChange={handleChange} />
                                    {(errors.bookingName && touched.bookingName) && <p className='text-xs text-red-400 mt-1'>{errors.bookingName}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Email Address<sup className="text-red-600">*</sup></label>
                                    <input type="email" id="bookingEmail" value={values.bookingEmail} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. jhon.deo@gmail.com" onChange={handleChange} />
                                    {(errors.bookingEmail && touched.bookingEmail) && <p className='text-xs text-red-400 mt-1'>{errors.bookingEmail}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Phone Number<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="bookingPhone" value={values.bookingPhone} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.bookingPhone && touched.bookingPhone) && <p className='text-xs text-red-400 mt-1'>{errors.bookingPhone}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Check-In Date<sup className="text-red-600">*</sup></label>
                                    <input type="date" id="checkIn" value={values.checkIn} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.checkIn && touched.checkIn) && <p className='text-xs text-red-400 mt-1'>{errors.checkIn}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Check-Out Date<sup className="text-red-600">*</sup></label>
                                    <input type="date" id="checkOut" value={values.checkOut} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.checkOut && touched.checkOut) && <p className='text-xs text-red-400 mt-1'>{errors.checkOut}</p>}
                                </div>

                                <div className="col-span-12 font-medium">
                                    <p>Room unit information</p>
                                    <div className='w-full h-[1px] bg-gray-400 mt-1'></div>
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Number of People<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="nop" value={values.nop} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. 1" onChange={handleChange} />
                                    {(errors.nop && touched.nop) && <p className='text-xs text-red-400 mt-1'>{errors.nop}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Number of Rooms<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="nor" value={values.nor} max={10} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. 1" onChange={(e) => {
                                        setSelectedRoomTypes([]);
                                        handleChange(e);
                                    }} />
                                    {(errors.nor && touched.nor) && <p className='text-xs text-red-400 mt-1'>{errors.nor}</p>}
                                </div>

                                {
                                    values.nor > 0 && < div className="col-span-12 grid grid-cols-10 gap-1">
                                        <label className="block mb-1 text-sm font-medium text-gray-600 col-span-12">Select Room Types<sup className="text-red-600">*</sup></label>
                                        {
                                            Array.from({ length: values.nor }, (_, i) => (
                                                <div key={i} className="space-y-1">
                                                    <label className="block mb-1 text-xs font-medium text-gray-900">Room {i + 1}</label>
                                                    <select type="text" value={selectedRoomTypes[i]} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" onChange={(value) => handleRoomTypeChange(i, value.target.value)}>
                                                        <option value="">Room type</option>
                                                        {
                                                            types.map((type) => {
                                                                return <option value={type._id}>{type.name}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                            ))
                                        }
                                    </div>
                                }

                                <div className="col-span-12 font-medium">
                                    <p>Payment information</p>
                                    <div className='w-full h-[1px] bg-gray-400 mt-1'></div>
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Booking Total (₹)<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="total" value={values.total} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.total && touched.total) && <p className='text-xs text-red-400 mt-1'>{errors.total}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Extra Charges (₹)<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="extraCharges" value={values.extraCharges} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.extraCharges && touched.extraCharges) && <p className='text-xs text-red-400 mt-1'>{errors.extraCharges}</p>}
                                </div>

                                {
                                    sub.features.isTax && <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Tax (%)<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="tax" value={values.tax} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                        {(errors.tax && touched.tax) && <p className='text-xs text-red-400 mt-1'>{errors.tax}</p>}
                                    </div>
                                }

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Discount (₹)<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="discount" value={values.discount} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.discount && touched.discount) && <p className='text-xs text-red-400 mt-1'>{errors.discount}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Grand Total (₹)<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="grand" value={values.grand} autoComplete='off' readOnly className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. x" onChange={handleChange} />
                                    {(errors.grand && touched.grand) && <p className='text-xs text-red-400 mt-1'>{errors.grand}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Advance Payment (₹)<sup className="text-red-600">*</sup></label>
                                    <input type="number" id="paid" value={values.paid} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                    {(errors.paid && touched.paid) && <p className='text-xs text-red-400 mt-1'>{errors.paid}</p>}
                                </div>

                                <div className="col-span-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-600">Payment Mode<sup className="text-red-600">*</sup></label>
                                    <select id="paymentMode" value={values.paymentMode} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" onChange={handleChange}>
                                        <option value="">Select Payment Mode</option>
                                        <option value="UPI">UPI</option>
                                        <option value="CASH">CASH</option>
                                        <option value="Online">Online</option>
                                        <option value="Regular">Regular</option>
                                        <option value="GoMMT">GoMMT</option>
                                        <option value="OYO Paid">OYO Paid</option>
                                        <option value="Booking.com">Booking.com</option>
                                        <option value="Agoda">Agoda</option>
                                        <option value="Website">Website</option>
                                    </select>
                                    {(errors.paymentMode && touched.paymentMode) && <p className='text-xs text-red-400 mt-1'>{errors.paymentMode}</p>}
                                </div>

                                <div className='col-span-12 my-6'>
                                    <div className='flex items-end'>
                                        <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                            {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                            {!submit && "SAVE"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        }
                    </div>
                </ScrollArea >
            </div >
        </div >
    )
}
